import { jsPDF } from 'jspdf'
import UPNG from 'upng-js'
import { loadImage, canvasToBlob, baseName } from './format'

// Each tool returns { blob, name, mime } so the result view can preview/download.

// Pick an output mime/ext that matches the source, so every tool keeps the
// original image type. Only Convert intentionally changes the format (and
// Remove Background forces PNG for transparency). Formats a canvas can't
// re-encode (gif/bmp/heic) fall back to lossless PNG.
function outputType(file) {
  const t = (file.type || '').toLowerCase()
  if (t === 'image/png') return { mime: 'image/png', ext: 'png' }
  if (t === 'image/webp') return { mime: 'image/webp', ext: 'webp' }
  if (t === 'image/jpeg') return { mime: 'image/jpeg', ext: 'jpg' }
  return { mime: 'image/png', ext: 'png' }
}

export async function compressImage(file, opts) {
  const img = await loadImage(file)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)
  const { mime, ext } = outputType(file)
  const quality = (opts.quality ?? 70) / 100

  let blob
  if (mime === 'image/png') {
    // PNG is lossless, so a plain re-encode won't shrink. Quantise the colours
    // with UPNG (lossy palette PNG) stays a PNG but gets much smaller. The
    // quality slider maps to how many colours we keep.
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const colours = Math.max(8, Math.round(quality * 256)) // 8..256
    const ab = UPNG.encode([data.buffer], canvas.width, canvas.height, colours)
    blob = new Blob([ab], { type: 'image/png' })
  } else {
    // JPEG / WebP re-encode at the chosen quality.
    blob = await canvasToBlob(canvas, mime, quality)
  }

  // Compress must never grow the file if the source was already smaller,
  // keep the original bytes so the size only ever stays the same or shrinks.
  if (blob.size >= file.size) blob = file
  return { blob, name: baseName(file.name) + '-compressed.' + ext, mime }
}

export async function resizeImage(file, opts) {
  const img = await loadImage(file)
  const w = Math.max(1, Math.round(opts.width || img.naturalWidth))
  const h = Math.max(1, Math.round(opts.height || img.naturalHeight))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, w, h)
  const { mime, ext } = outputType(file)
  const blob = await canvasToBlob(canvas, mime, 0.92)
  return { blob, name: `${baseName(file.name)}-${w}x${h}.${ext}`, mime }
}

// opts.crop is the completed pixel crop relative to the displayed image,
// with scaleX / scaleY to map back to natural pixels.
export async function cropImage(file, opts) {
  const img = await loadImage(file)
  const c = opts.crop
  if (!c || !c.width || !c.height) throw new Error('Select an area to crop first.')
  const sx = c.x * c.scaleX
  const sy = c.y * c.scaleY
  const sw = c.width * c.scaleX
  const sh = c.height * c.scaleY
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(sw)
  canvas.height = Math.round(sh)
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
  const { mime, ext } = outputType(file)
  const blob = await canvasToBlob(canvas, mime, 0.92)
  return { blob, name: `${baseName(file.name)}-cropped.${ext}`, mime }
}

export async function convertImage(file, opts) {
  const img = await loadImage(file)
  const fmt = (opts.format || 'PNG').toLowerCase()
  const map = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp' }
  const mime = map[fmt] || 'image/png'
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  if (mime === 'image/jpeg') {
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
  }
  ctx.drawImage(img, 0, 0)
  const blob = await canvasToBlob(canvas, mime, 0.92)
  const ext = fmt === 'jpeg' ? 'jpg' : fmt
  return { blob, name: `${baseName(file.name)}.${ext}`, mime }
}

export async function rotateImage(file, opts) {
  const img = await loadImage(file)
  const t = opts.transform || 'Rotate right'
  const w = img.naturalWidth
  const h = img.naturalHeight
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const quarter = t === 'Rotate left' || t === 'Rotate right'
  canvas.width = quarter ? h : w
  canvas.height = quarter ? w : h
  ctx.save()
  if (t === 'Rotate right') {
    ctx.translate(h, 0)
    ctx.rotate(Math.PI / 2)
  } else if (t === 'Rotate left') {
    ctx.translate(0, w)
    ctx.rotate(-Math.PI / 2)
  } else if (t === 'Flip H') {
    ctx.translate(w, 0)
    ctx.scale(-1, 1)
  } else if (t === 'Flip V') {
    ctx.translate(0, h)
    ctx.scale(1, -1)
  }
  ctx.drawImage(img, 0, 0)
  ctx.restore()
  const { mime, ext } = outputType(file)
  const blob = await canvasToBlob(canvas, mime, 0.92)
  return { blob, name: `${baseName(file.name)}-rotated.${ext}`, mime }
}

// Rotate a loaded image by an arbitrary 90° multiple — used for the PDF rotate
// live preview. Returns a PNG data URL.
export function rotatedPreviewDeg(img, deg) {
  const maxSide = 1100
  const s = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight))
  const w = Math.round(img.naturalWidth * s)
  const h = Math.round(img.naturalHeight * s)
  const swap = ((deg % 360) + 360) % 180 !== 0
  const canvas = document.createElement('canvas')
  canvas.width = swap ? h : w
  canvas.height = swap ? w : h
  const ctx = canvas.getContext('2d')
  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((deg * Math.PI) / 180)
  ctx.drawImage(img, -w / 2, -h / 2, w, h)
  return canvas.toDataURL('image/png')
}

// Fast, downscaled preview of a rotate/flip transform for the live tool view.
// Takes a loaded HTMLImageElement and returns a PNG data URL.
export function rotatedPreview(img, transform) {
  const maxSide = 800
  const s = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight))
  const w = Math.round(img.naturalWidth * s)
  const h = Math.round(img.naturalHeight * s)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const quarter = transform === 'Rotate left' || transform === 'Rotate right'
  canvas.width = quarter ? h : w
  canvas.height = quarter ? w : h
  ctx.save()
  if (transform === 'Rotate right') {
    ctx.translate(h, 0)
    ctx.rotate(Math.PI / 2)
  } else if (transform === 'Rotate left') {
    ctx.translate(0, w)
    ctx.rotate(-Math.PI / 2)
  } else if (transform === 'Flip H') {
    ctx.translate(w, 0)
    ctx.scale(-1, 1)
  } else if (transform === 'Flip V') {
    ctx.translate(0, h)
    ctx.scale(1, -1)
  }
  ctx.drawImage(img, 0, 0, w, h)
  ctx.restore()
  return canvas.toDataURL('image/png')
}

export async function upscaleImage(file, opts) {
  const img = await loadImage(file)
  const factor = parseInt(opts.scale || '2', 10)
  const w = img.naturalWidth * factor
  const h = img.naturalHeight * factor
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0, w, h)
  const { mime, ext } = outputType(file)
  const blob = await canvasToBlob(canvas, mime, 0.95)
  return { blob, name: `${baseName(file.name)}-${factor}x.${ext}`, mime }
}

export async function watermarkImage(file, opts) {
  const img = await loadImage(file)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)
  const text = opts.text || '© iLoveImgPdf'
  const pos = opts.position || 'Center'
  const fontSize = Math.max(16, Math.round(canvas.width / 18))
  ctx.font = `600 ${fontSize}px Inter, sans-serif`
  ctx.fillStyle = 'rgba(255,255,255,.55)'
  ctx.strokeStyle = 'rgba(0,0,0,.25)'
  ctx.lineWidth = Math.max(1, fontSize / 20)
  ctx.textBaseline = 'middle'

  const draw = (x, y, align) => {
    ctx.textAlign = align
    ctx.strokeText(text, x, y)
    ctx.fillText(text, x, y)
  }

  if (pos === 'Tiled') {
    ctx.save()
    ctx.globalAlpha = 0.85
    ctx.rotate(-Math.PI / 8)
    const step = fontSize * 6
    for (let y = -canvas.height; y < canvas.height * 2; y += step) {
      for (let x = -canvas.width; x < canvas.width * 2; x += step * 1.6) {
        draw(x, y, 'left')
      }
    }
    ctx.restore()
  } else if (pos === 'Top-left') {
    draw(fontSize, fontSize, 'left')
  } else {
    draw(canvas.width / 2, canvas.height / 2, 'center')
  }

  const { mime, ext } = outputType(file)
  const blob = await canvasToBlob(canvas, mime, 0.92)
  return { blob, name: `${baseName(file.name)}-watermarked.${ext}`, mime }
}

// AI background removal (@imgly/background-removal). A segmentation model runs
// in the browser and erases only the background — the subject keeps its full
// colour and sharpness (no colour key, no posterising, no greyscale). The model
// is fetched on first use, so this call can take a few seconds.
export async function removeBackground(file, opts, onProgress) {
  const mod = await import('@imgly/background-removal')
  const imglyRemove = mod.removeBackground || mod.default
  const blob = await imglyRemove(file, {
    output: { format: 'image/png', quality: 1 },
    progress: (key, current, total) => {
      if (onProgress && total) onProgress(Math.round((current / total) * 100))
    },
  })
  return { blob, name: `${baseName(file.name)}-nobg.png`, mime: 'image/png' }
}

// Blur the chosen rectangles (faces / private areas). Regions are page-relative
// ratios; the rest of the image is untouched.
export async function blurRegions(file, opts) {
  const img = await loadImage(file)
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0)

  const regions = opts.regions || []
  if (!regions.length) throw new Error('Drag a box over the area to blur first.')
  const blurPx = Math.max(3, Math.round((opts.strength ?? 18) * (canvas.width / 600)))

  regions.forEach((r) => {
    const x = r.xRatio * canvas.width
    const y = r.yRatio * canvas.height
    const w = r.wRatio * canvas.width
    const h = r.hRatio * canvas.height
    ctx.save()
    ctx.beginPath()
    ctx.rect(x, y, w, h)
    ctx.clip()
    ctx.filter = `blur(${blurPx}px)`
    ctx.drawImage(img, 0, 0)
    ctx.filter = 'none'
    ctx.restore()
  })

  const { mime, ext } = outputType(file)
  const blob = await canvasToBlob(canvas, mime, 0.92)
  return { blob, name: `${baseName(file.name)}-blurred.${ext}`, mime }
}

export async function imagesToPdf(files, opts) {
  const size = opts.pagesize || 'A4'
  const orient = (opts.orientation || 'Portrait') === 'Landscape' ? 'l' : 'p'
  let doc
  if (size === 'Fit to image') {
    doc = null // create per-page below
  } else {
    doc = new jsPDF({ orientation: orient, unit: 'pt', format: size === 'Letter' ? 'letter' : 'a4' })
  }

  for (let i = 0; i < files.length; i++) {
    const img = await loadImage(files[i])
    const dataUrl = await fileToDataUrl(files[i], img)
    if (size === 'Fit to image') {
      const o = img.naturalWidth >= img.naturalHeight ? 'l' : 'p'
      if (!doc) doc = new jsPDF({ orientation: o, unit: 'px', format: [img.naturalWidth, img.naturalHeight] })
      else doc.addPage([img.naturalWidth, img.naturalHeight], o)
      doc.addImage(dataUrl.url, dataUrl.fmt, 0, 0, img.naturalWidth, img.naturalHeight)
    } else {
      if (i > 0) doc.addPage()
      const pw = doc.internal.pageSize.getWidth()
      const ph = doc.internal.pageSize.getHeight()
      const margin = 24
      const maxW = pw - margin * 2
      const maxH = ph - margin * 2
      const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight)
      const w = img.naturalWidth * ratio
      const h = img.naturalHeight * ratio
      doc.addImage(dataUrl.url, dataUrl.fmt, (pw - w) / 2, (ph - h) / 2, w, h)
    }
  }
  const blob = doc.output('blob')
  return { blob, name: `${baseName(files[0].name)}-images.pdf`, mime: 'application/pdf' }
}

// Re-encode every image as JPEG data URL so jsPDF embeds reliably.
function fileToDataUrl(file, img) {
  const canvas = document.createElement('canvas')
  canvas.width = img.naturalWidth
  canvas.height = img.naturalHeight
  const ctx = canvas.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0)
  return Promise.resolve({ url: canvas.toDataURL('image/jpeg', 0.92), fmt: 'JPEG' })
}
